// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.QuerySourceFieldDetailsStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

// should be consistent to backend
import AppGlobal from 'Izenda.AppGlobal';
import { APPROVAL_LEVEL, DETECTED_CHANGE_LEVEL } from 'logic/data/Izenda.DataModelConstants';
import { FILTER_LOOKUP_STATUSES } from 'logic/data/Izenda.FilterLookupConstants';
import ObjectStateConstants from 'logic/data/Izenda.ObjectStateConstants';
import { FIELD_TYPE } from 'logic/data/Izenda.QuerySourceTypeConfig';
import { action, computed, observable } from 'mobx';
import tracker from 'share/tracking/Izenda.Tracker';

@tracker
export default class QuerySourceFieldDetailsStore {
  @observable
  visibleChanged: boolean = false;

  @observable
  filterableChanged: boolean = false;

  @observable
  details: Object = null;

  @observable
  aliasError: string = '';

  /**
   * Is visible
   */
  @computed
  get isVisible() {
    return this.details.visible;
  }

  /**
   * Is filterable
   */
  @computed
  get isFilterable() {
    return this.details.filterable;
  }

  /**
   * Is match tenant
   */
  @computed
  get isMatchTenant() {
    return this.details.matchedTenant;
  }

  /**
   * Is parameter
   */
  @computed
  get isParameter() {
    return this.details.type === FIELD_TYPE.PARAM;
  }

  /**
   * Is disable visible
   */
  @computed
  get isDisabledVisible() {
    return (
      (this.details.matchedTenant && !this.showTenantFieldValue) ||
      this.details.physicalChange === DETECTED_CHANGE_LEVEL.DELETED
    );
  }

  /**
   * Is diasble filterable
   */
  @computed
  get isDisabledFilterable() {
    return (
      this.details.isCompositeField ||
      this.details.isRunningField ||
      (this.details.matchedTenant && !this.showTenantFieldValue) ||
      !this.details.visible
    );
  }

  /**
   * Is disable filter lookup
   */
  @computed
  get isDisableFilterLookup() {
    return this.details.physicalChange === DETECTED_CHANGE_LEVEL.DELETED;
  }

  /**
   * Is change to invisible
   */
  @computed
  get isChangeToInvisible() {
    return this.isVisible == false && this.visibleChanged == true;
  }

  /**
   * Is change to unfilterable
   */
  @computed
  get isChangeToUnfilterable() {
    return this.isFilterable == false && this.filterableChanged == true;
  }

  /**
   * Is new field
   */
  @computed
  get isNewField() {
    return (
      this.details.approval < APPROVAL_LEVEL.QUERY_SOURCE_FIELD &&
      this.details.physicalChange === DETECTED_CHANGE_LEVEL.ADDED
    );
  }

  /**
   * Is invalid filter lookup
   */
  @computed
  get isInvalidFilterLookup() {
    return this.details.filterLookupStatus !== FILTER_LOOKUP_STATUSES.VALID;
  }

  constructor(details: any) {
    this.showTenantFieldValue = AppGlobal.advancedSettings.security.showTenantFieldValue;
    details.filteredValue = JSON.parse(details.filteredValue || '{}');
    this.details = details;
  }

  /**
   * Get data type
   */
  getDataType() {
    return this.details.dataType || this.details.izendaDataType;
  }

  /**
   * Get column name
   */
  getColumnName() {
    return this.details.name;
  }

  /**
   * Get column alias
   */
  getColumnAlias() {
    return this.details.alias;
  }

  /**
   * Get filter lookup status
   */
  getFilterLookupStatus() {
    return this.details.filterLookupStatus;
  }

  /**
   * Set column alias
   * @param {string} newAlias 
   */
  @action
  setColumnAlias(newAlias: string) {
    this.aliasError && (this.aliasError = '');
    this.details.alias = newAlias;
    this.setUpdateState();
    this.markDirty();
  }

  /**
   * Set update state
   */
  setUpdateState() {
    this.details.state === ObjectStateConstants.NONE && (this.details.state = ObjectStateConstants.UPDATE);
  }

  /**
   * Get filter values
   */
  getFilterValues() {
    return this.details.filteredValue;
  }

  /**
   * Set filter values
   * @param {string} newValue 
   */
  @action
  setFilterValues(newValue: any) {
    this.details.filteredValue = newValue;
    this.markDirty();
  }

  /**
   * Set visible
   * @param {boolean} newValue 
   */
  @action
  setVisible(newValue: boolean) {
    this.details.visible = newValue;
    !newValue && (this.details.filterable = newValue);
    this.setUpdateState();
    this.markDirty();
    this.markVisibeDirty();
  }

  /**
   * Set filterable
   * @param {boolean} newValue 
   */
  @action
  setFilterable(newValue) {
    this.details.filterable = newValue;
    this.setUpdateState();
    this.markDirty();
    this.markFilterableDirty();
  }

  /**
   * Mark visible dirty
   */
  markVisibeDirty() {
    this.visibleChanged = true;
  }

  /**
   * Mark filterable dirty
   */
  markFilterableDirty() {
    this.filterableChanged = true;
  }

  /**
   * Get details
   */
  getDetails() {
    return this.details;
  }

  /**
   * Set valid filter lookup
   */
  @action
  setValidFilterLookup() {
    this.details.filterLookupStatus = FILTER_LOOKUP_STATUSES.VALID;
  }
}



// WEBPACK FOOTER //
// ./ui/logic/Izenda.QuerySourceFieldDetailsStore.js